import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../db';
import { useLiveQuery } from 'dexie-react-hooks';

interface User {
  id: number;
  billingStatus?: string;
  accessTags?: string | string[];
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  currentProgram?: string;
  role?: string;
  [key: string]: any;
}

const BillingContext = createContext<any>(null);

export const useBilling = () => useContext(BillingContext);

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [billingStatus, setBillingStatus] = useState('Unknown');
  const [accessTags, setAccessTags] = useState<string[]>([]);
  const [trialStart, setTrialStart] = useState<string | null>(null);

  // Live query for user data
  const user = useLiveQuery<any>(() => db.user.orderBy('id').last(), []);

  // Sync billing status from user record
  useEffect(() => {
    if (user) {
      setBillingStatus(user.billingStatus || 'Unknown');
      const tags = user.accessTags;
      if (Array.isArray(tags)) {
        setAccessTags(tags);
      } else if (typeof tags === 'string') {
        setAccessTags(tags.split(',').map(t => t.trim()).filter(Boolean));
      } else {
        setAccessTags([]);
      }
      setTrialStart(user.trialStartDate || null);
    }
  }, [user]);

  // Premium status check
  const isPremium = ['Active', 'VIP', 'Premium'].some(
    s => billingStatus.toLowerCase().includes(s.toLowerCase())
  );

  // Trial days remaining
  const trialDaysRemaining = (() => {
    if (!trialStart) {return 0;}
    const now = new Date();
    const start = new Date(trialStart);

    // Use user.trialEndDate if available (High Fidelity), else fallback to 14 days
    const end = user?.trialEndDate ? new Date(user.trialEndDate) : new Date(start.getTime() + (14 * 24 * 60 * 60 * 1000));

    const diffMs = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  })();

  // Feature access check ... (no change)
  const hasAccess = (featureTag: string) => {
    const role = localStorage.getItem('USER_ROLE') || user?.role || '';
    if (role.toUpperCase() === 'ADMIN') {return true;}
    if (!isPremium && trialDaysRemaining <= 0) {return false;}
    if (featureTag && accessTags.length > 0) {
      return accessTags.some(tag => tag.toLowerCase() === featureTag.toLowerCase() || tag.toLowerCase() === 'all');
    }
    return isPremium;
  };

  // Billing status display - Surgically Enhanced for Roles & Trials
  const getStatusDisplay = () => {
    const role = (localStorage.getItem('USER_ROLE') || user?.role || '').toUpperCase();

    if (role === 'ADMIN') {return { label: 'Sovereign Administrator', color: 'blue' };}
    if (role === 'COACH') {return { label: 'Certified Performance Coach', color: 'green' };}
    if (role === 'PARTNER') {return { label: 'Revenue Partner', color: 'orange' };}

    if (billingStatus === 'Active') {return { label: user?.currentProgram || 'Full Access', color: 'green' };}

    if (billingStatus === 'Trial' || trialDaysRemaining > 0) {
      const days = trialDaysRemaining;
      const color = days < 3 ? 'red' : days < 7 ? 'orange' : 'blue';
      return { label: `Trial: ${days} days remaining`, color };
    }

    if (billingStatus === 'Cancelled') {return { label: 'Account Suspended', color: 'red' };}
    if (billingStatus === 'Overdue') {return { label: 'Payment Overdue', color: 'orange' };}

    return { label: billingStatus || 'Visitor', color: 'gray' };
  };

  return (
    <BillingContext.Provider value={{
      user,
      billingStatus,
      isPremium,
      trialDaysRemaining,
      accessTags,
      hasAccess,
      getStatusDisplay,
      trialEndDate: user?.trialEndDate || new Date(new Date(trialStart!).getTime() + (14 * 24 * 60 * 60 * 1000)).toISOString()
    }}>
      {children}
    </BillingContext.Provider>
  );
};

export default BillingContext;